import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  LocationCreation,
  LocationPayloadModel,
  LocationResponseModel,
  LocationUpdateModel,
  LocationUploadResponseModel,
} from '@core/models';
import { SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS } from '@core/interceptors/http-error.interceptor';
import { ApiService } from '@core/services/api.service';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  constructor(private apiService: ApiService) {}

  getListLocationsFilter(filter: LocationPayloadModel): Observable<LocationResponseModel> {
    return this.apiService.get('/locations', filter as any).pipe(
      map(response => {
        return response;
      }),
    );
  }

  getLocationDetail(locationId: string): Observable<any> {
    return this.apiService.get(`/locations/${locationId}`).pipe(
      map(response => {
        return response;
      }),
    );
  }

  createLocation(location: LocationCreation, partnerOid: string): Observable<any> {
    return this.apiService.post(`/locations/${partnerOid}`, location).pipe(
      map(response => {
        return response;
      }),
    );
  }

  updateLocation(location: LocationUpdateModel, locationId: number): Observable<any> {
    // A 409 names the refusal in its detail, which the store explains in a translated message.
    return this.apiService.putRaw<unknown>(
      `/locations/${locationId}`,
      location,
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS, [409]),
    );
  }

  /**
   * Validate (and optionally create) locations from a CSV in one call.
   *
   * With `create: true`, the backend creates every row in a single all-or-nothing transaction,
   * attaches each to `installationGroupOids`, and writes the new `locationId` back onto each
   * returned row. Without it, the file is only validated.
   */
  createLocationBulk(
    file: File,
    partnerOid: string,
    options?: { create?: boolean; installationGroupOids?: string[] },
  ): Observable<LocationUploadResponseModel[]> {
    const formData = new FormData();

    formData.append('file', file, file.name);
    formData.append('partnerOid', partnerOid);
    if (options?.create) {
      formData.append('create', 'true');
    }
    // Sent on validation too (not just create) so the backend can check group access up front.
    for (const oid of options?.installationGroupOids ?? []) {
      formData.append('installationGroupOids', oid);
    }
    return this.apiService.post(`/locations/bulk-by-partner`, formData);
  }

  getLocationsByDeviceId(deviceId: string): Observable<any> {
    const payload = {
      deviceId,
      limit: 5,
      offset: 0,
    };
    return this.apiService.get('/locations', payload as any).pipe(
      map(response => {
        return response;
      }),
    );
  }
}
